import { portalAPI } from './portalAPI';

import type {
    Employee,
    EmployeeCreateDto,
    EmployeeListItemMinDto,
    EmployeeSystemRolesDto,
    EmployeeTargetsDto,
    EmployeeUpdateDto,
} from 'types/employees';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

const APIEmployeesSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Employees', 'EmployeesMin', 'HistoryListItems', 'EmployeeWeb'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getEmployeeById: builder.query<Employee, string>({
                query: (id) => `/v1/employees/${id}`,
                providesTags: (_1, _2, arg) => [{ type: 'Employees', id: arg }],
                keepUnusedDataFor: 0,
            }),
            // The id code behind the asterisks the detail reads while obfuscated, for the one view that shows it.
            getEmployeePersonalIdCode: builder.query<{ value: string | null }, string>({
                query: (id) => `/v1/employees/${id}/personal-id-code`,
                keepUnusedDataFor: 0,
            }),
            getEmployeesListWithFilters: accumulatedQueryWithFilters<EmployeeListItemMinDto>(
                builder,
                '/v1/employees/min',
                {
                    passiveFilters: 'isActive==true',
                    providesTags: ['EmployeesMin'],
                }
            ),
            getAllEmployeesListWithFilters: accumulatedQueryWithFilters<EmployeeListItemMinDto>(
                builder,
                '/v1/employees/min',
                {
                    providesTags: ['EmployeesMin'],
                }
            ),
            getEmployeesWithFilters: accumulatedQueryWithFilters<Employee>(builder, '/v1/employees', {
                providesTags: ['Employees'],
            }),
            getAllEmployees: builder.query<Employee[], void>({
                query: () => `/v1/employees`,
                providesTags: ['Employees', 'EmployeesMin'],
                keepUnusedDataFor: 60, // Cache for 60 seconds
            }),
            addEmployeeRoles: builder.mutation<Employee, EmployeeSystemRolesDto[]>({
                query: (body) => ({
                    url: `/v1/employees/roles/add`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['Employees', 'EmployeesMin'],
            }),
            removeEmployeeRoles: builder.mutation<Employee, EmployeeSystemRolesDto[]>({
                query: (body) => ({
                    url: `/v1/employees/roles/remove`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['Employees', 'EmployeesMin'],
            }),
            addEmployee: builder.mutation<Employee, EmployeeCreateDto>({
                query: ({ ...body }) => ({
                    url: `/v1/employees`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['Employees', 'EmployeesMin'],
            }),
            updateEmployee: builder.mutation<Employee, EmployeeUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/employees/${id}`,
                    method: 'PUT',
                    body,
                }),
                // Most of the web profile is the employee's own data, so it reads anew too.
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              'HistoryListItems',
                              'Employees',
                              'EmployeesMin',
                              { type: 'Employees', id: arg.id },
                              { type: 'EmployeeWeb', id: String(arg.id) },
                          ]
                        : ['HistoryListItems', 'Employees', 'EmployeesMin'],
            }),
            deleteEmployee: builder.mutation<boolean, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/employees/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Employees', 'EmployeesMin'],
            }),
            setMyVacation: builder.mutation<Employee, boolean>({
                query: (onVacation) => ({
                    url: `/v1/employees/me/vacation`,
                    method: 'PUT',
                    params: { onVacation },
                }),
                invalidatesTags: ['Employees', 'EmployeesMin'],
            }),
            // Targets show on no list, so only the caller's own getEmployeeById entry refetches.
            setMyTargets: builder.mutation<Employee, EmployeeTargetsDto>({
                query: (body) => ({
                    url: `/v1/employees/me/targets`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (result) => (result ? [{ type: 'Employees', id: String(result.id) }] : []),
            }),
        }),
        overrideExisting: false,
    });

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetEmployeeByIdQuery,
    useGetEmployeesListWithFiltersQuery,
    useGetAllEmployeesListWithFiltersQuery,
    useGetAllEmployeesQuery,
    useLazyGetAllEmployeesListWithFiltersQuery,
    useLazyGetEmployeePersonalIdCodeQuery,
    useLazyGetEmployeesListWithFiltersQuery,
    useLazyGetEmployeesWithFiltersQuery,
    useAddEmployeeMutation,
    useAddEmployeeRolesMutation,
    useRemoveEmployeeRolesMutation,
    useUpdateEmployeeMutation,
    useSetMyVacationMutation,
    useSetMyTargetsMutation,
} = APIEmployeesSlice;
